import { FC, useCallback, useContext, useMemo } from 'react';

import { getStateValueStrings } from '@eneco/common/xstate/stateValue';
import { ChatContext, useChat } from '@eneco/components/chat/useChat';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { AppHeader } from '@eneco/components-next/AppHeader/AppHeader';
import useDC from '@eneco/dc/useDC';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { useLinkComponent } from '@eneco/link';
import { Box, Button, ButtonLink, IconButton, NavLink, Stack } from '@eneco/sparky';
import { ChevronLeftIcon, CloseIcon } from '@eneco/sparky/icons';
import type { ProgressIndicatorItemProps } from '@eneco/sparky/types';

import { useMeterReadingsFlow } from '../state/MeterReadingsFlow.machine';

export const Header: FC = () => {
  const { label } = useDC();

  return (
    <I18nProvider dictionary={locale => import(`./content/Header.${label}.${locale}.json`)}>
      <HeaderComponent />
    </I18nProvider>
  );
};

const ChatButton: FC = () => {
  const { t } = useTranslation();
  const [mount] = useChat();

  return (
    <TrackedDialog.Close asChild>
      <Button onClick={() => mount({ visibility: 'open', topic: 'start_meterstandenalgemeen' })} action="primary">
        {t('closeDialogCancelButtonLabel')}
      </Button>
    </TrackedDialog.Close>
  );
};

const HeaderComponent: FC = () => {
  const Link = useLinkComponent();
  const { t } = useTranslation();
  const { state, send } = useMeterReadingsFlow();
  const hasChat = !!useContext(ChatContext);

  const logoLink = t('logoLink.href');
  const onCloseConfirmLink = state.context.isAuthenticated
    ? t('authorizedCloseDialogConfirmLink.href')
    : t('closeDialogConfirmLink.href');

  const steps: ProgressIndicatorItemProps[] = useMemo(() => {
    const stateValues = getStateValueStrings(state.value);

    return state.context.steps.map(({ state: stepState }: { state: string }, index: number, steps: { state: string }[]) => ({
      isActive: stateValues.includes(stepState),
      isDisabled: steps.findIndex((step: { state: string }) => stateValues.includes(step.state)) < index,
      children: t(`${stepState}.title`),
      onClick: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = { type: 'GOTO_STEP', target: stepState };
        send(payload);
      },
    }));
  }, [send, state, t]);

  const onPrevious = useCallback(() => send({ type: 'PREVIOUS_STEP' }), [send]);

  return (
    <AppHeader
      logoLink={logoLink}
      steps={steps}
      previousButton={
        state.can({ type: 'PREVIOUS_STEP' }) && (
          <NavLink onClick={onPrevious} variant="secondary" leftIcon={<ChevronLeftIcon />}>
            {t('previousButtonLabel')}
          </NavLink>
        )
      }
      closeButton={
        <Link href={onCloseConfirmLink} linkType="external">
          <IconButton label={t('closeButtonLabel')}>
            <CloseIcon />
          </IconButton>
        </Link>
      }
      dialog={
        <TrackedDialog
          trigger={
            <IconButton label={t('closeDialogConfirmLink.text')}>
              <CloseIcon />
            </IconButton>
          }
          title={t('closeDialogTitle')}
          description={<RichText html={t('closeDialogContent')} />}>
          <Box paddingTop="8">
            <Stack direction={{ initial: 'column', md: 'row' }} gap="5">
              {hasChat ? (
                <ChatButton />
              ) : (
                <TrackedDialog.Close asChild>
                  <Button action="primary">{t('closeDialogCancelButtonLabel')}</Button>
                </TrackedDialog.Close>
              )}
              <Link href={onCloseConfirmLink} linkType="external">
                <ButtonLink action="secondary">{t('closeDialogConfirmLink.text')}</ButtonLink>
              </Link>
            </Stack>
          </Box>
        </TrackedDialog>
      }
    />
  );
};
